import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import './Pages.css';
import GoogleIntegrationCard from '../components/GoogleIntegrationCard';

const STATUSES = ['pending', 'awaiting_payment', 'confirmed', 'completed', 'rejected'];

const PractitionerDashboard = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionId, setActionId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role !== 'practitioner' && user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/bookings`, { headers });
      setBookings(response.data || []);
    } catch (err) {
      setError('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (id, action) => {
    setActionId(id);
    try {
      await axios.patch(`${apiUrl}/bookings/${id}/${action}`, {}, { headers });
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} booking`);
    } finally {
      setActionId(null);
    }
  };

  const countOf = (status) => bookings.filter((b) => b.status === status).length;
  const filtered = bookings.filter((b) => b.status === filter);

  if (loading) return <div className="page-container"><p className="text-muted">Loading dashboard...</p></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Practitioner Dashboard</h1>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div className="stat-tile">
          <div className="stat-value">{countOf('pending')}</div>
          <div className="stat-label">Pending Approval</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">{countOf('awaiting_payment')}</div>
          <div className="stat-label">Awaiting Payment</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">{countOf('confirmed')}</div>
          <div className="stat-label">Upcoming (Confirmed)</div>
        </div>
        <div className="stat-tile">
          <div className="stat-value">{countOf('completed')}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      <div className="flex-between" style={{ margin: '20px 0', flexWrap: 'wrap', gap: 10 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/services')}>Manage Services</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/crm')}>View Clients</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/invoices')}>View Invoices</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/short-urls')}>Generate Links</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/availability')}>Set Availability</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/campaigns')}>Email Campaigns</button>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/reports')}>Reports</button>
      </div>

      <div className="tabs">
        {STATUSES.map((s) => (
          <button key={s} className={`tab-btn ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
            {s.replace('_', ' ')} ({countOf(s)})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="empty-state">No {filter.replace('_', ' ')} sessions</p>
      ) : (
        <div className="list-container">
          {filtered.map((booking) => (
            <div key={booking.id} className="card">
              <div className="flex-between">
                <div>
                  <strong>{booking.service_name}</strong>
                  <p className="text-muted" style={{ margin: '4px 0' }}>
                    {booking.client_name} · {new Date(booking.booking_date).toLocaleString()} · ${parseFloat(booking.amount).toFixed(2)}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {booking.invoice_status && (
                    <span className={`badge badge-${booking.invoice_status}`}>invoice: {booking.invoice_status}</span>
                  )}
                  <span className={`badge badge-${booking.status}`}>{booking.status.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="flex-between" style={{ marginTop: 12 }}>
                <Link to={`/bookings/${booking.id}`} className="btn btn-secondary btn-sm">View / Notes</Link>
                <div style={{ display: 'flex', gap: 8 }}>
                  {booking.status === 'pending' && (
                    <>
                      <button className="btn btn-success btn-sm" disabled={actionId === booking.id} onClick={() => runAction(booking.id, 'approve')}>Approve</button>
                      <button className="btn btn-danger btn-sm" disabled={actionId === booking.id} onClick={() => runAction(booking.id, 'reject')}>Reject</button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button className="btn btn-secondary btn-sm" disabled={actionId === booking.id} onClick={() => runAction(booking.id, 'complete')}>Mark Completed</button>
                  )}
                  {(booking.status === 'awaiting_payment' || booking.status === 'confirmed') && (
                    <button className="btn btn-danger btn-sm" disabled={actionId === booking.id} onClick={() => runAction(booking.id, 'cancel')}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <GoogleIntegrationCard
        apiUrl={apiUrl}
        token={token}
        title="Google Calendar & Gmail"
        description="Connect your Google account to send booking emails from your own inbox and sync confirmed sessions to your calendar."
      />
    </div>
  );
};

export default PractitionerDashboard;
